import { Injectable } from '@angular/core';
import { Auth } from 'aws-amplify';
import { StorageKeys } from '@/shared/helper';

/** Canal para preguntarle a las demás pestañas del admin si siguen vivas. */
const TAB_CHANNEL = 'ares-admin-tabs';
const PING = 'tab:ping';
const ALIVE = 'tab:alive';
/** Ventana de respuesta al ping. Es local (misma máquina, mismo proceso): 200 ms sobra. */
const PING_TIMEOUT_MS = 200;

/**
 * Ata la sesión a la pestaña: cerrar la pestaña (o el navegador) cierra la sesión.
 *
 * Cognito guarda sus tokens en `localStorage`, que sobrevive al cierre de la pestaña; lo que no
 * sobrevive es `sessionStorage`. Por eso `StorageKeys.SESSION` sirve de marca de "esta pestaña es
 * dueña de una sesión": aguanta un F5 pero muere al cerrar la pestaña. Si al arrancar hay tokens
 * de Cognito y no hay marca, la sesión quedó huérfana y se cierra ANTES de que el router evalúe
 * los guards (por eso corre como `provideAppInitializer`, no desde un componente).
 *
 * El matiz son las pestañas hermanas: abrir el admin en una pestaña nueva también llega sin marca,
 * y como `localStorage` es compartido, cerrar sesión ahí tumbaría la sesión de la pestaña que ya
 * estaba trabajando. Por eso antes de matar nada se lanza un ping por `BroadcastChannel`: si alguna
 * otra pestaña con sesión contesta, la nueva adopta la sesión en vez de cerrarla. Solo cuando no
 * queda ninguna viva (es decir, todas se cerraron) la sesión se considera terminada.
 */
@Injectable({ providedIn: 'root' })
export class TabSessionService {
    private channel: BroadcastChannel | null = null;

    /** Corre una sola vez, al arrancar la app, antes de la navegación inicial. */
    async enforceTabScopedSession(): Promise<void> {
        try {
            if (!this.tabOwnsSession()) {
                const hasStoredSession = await this.hasStoredSession();

                if (hasStoredSession) {
                    if (await this.hasLiveSiblingTab()) {
                        // Otra pestaña del admin sigue abierta con sesión: esta la hereda.
                        sessionStorage.setItem(StorageKeys.SESSION, 'true');
                    } else {
                        // Se cerraron todas las pestañas con la sesión abierta.
                        await this.closeOrphanSession();
                    }
                } else {
                    localStorage.removeItem(StorageKeys.FORCE_LOGOUT);
                }
            }
        } catch (error) {
            console.error('Error validando la sesión de la pestaña:', error);
        } finally {
            // Se escucha siempre, incluso sin sesión todavía: la pestaña puede loguearse después
            // y a partir de ahí tiene que contestarle a las que abran luego.
            this.listenForPings();
        }
    }

    private tabOwnsSession(): boolean {
        return sessionStorage.getItem(StorageKeys.SESSION) === 'true';
    }

    /** ¿Quedaron credenciales en `localStorage` de una sesión anterior? */
    private async hasStoredSession(): Promise<boolean> {
        if (localStorage.getItem(StorageKeys.AUTH_USER)) {
            return true;
        }
        // Sin `auth_user` todavía pueden quedar tokens de Amplify (p.ej. si la pestaña se cerró
        // entre el signIn y el MFA), y esos son los que de verdad abren la sesión.
        return !!(await Auth.currentAuthenticatedUser().catch(() => null));
    }

    private async closeOrphanSession(): Promise<void> {
        await Auth.signOut().catch((error) => console.error('Error cerrando la sesión huérfana:', error));
        localStorage.removeItem(StorageKeys.AUTH_USER);
        localStorage.removeItem(StorageKeys.FORCE_LOGOUT);
        sessionStorage.removeItem(StorageKeys.SESSION);
    }

    private async hasLiveSiblingTab(): Promise<boolean> {
        if (typeof BroadcastChannel === 'undefined') {
            // Sin BroadcastChannel no se puede distinguir "pestaña nueva" de "pestaña reabierta";
            // se asume que hay hermanas vivas para no tumbarle la sesión a nadie por error.
            return true;
        }

        const channel = new BroadcastChannel(TAB_CHANNEL);
        try {
            return await new Promise<boolean>((resolve) => {
                const timeoutId = setTimeout(() => resolve(false), PING_TIMEOUT_MS);
                channel.onmessage = (event: MessageEvent) => {
                    if (event.data === ALIVE) {
                        clearTimeout(timeoutId);
                        resolve(true);
                    }
                };
                // BroadcastChannel no se entrega a sí mismo, así que nadie se autocontesta.
                channel.postMessage(PING);
            });
        } finally {
            channel.close();
        }
    }

    private listenForPings(): void {
        if (typeof BroadcastChannel === 'undefined' || this.channel) {
            return;
        }

        this.channel = new BroadcastChannel(TAB_CHANNEL);
        this.channel.onmessage = (event: MessageEvent) => {
            if (event.data === PING && this.tabOwnsSession()) {
                this.channel?.postMessage(ALIVE);
            }
        };
    }
}
